import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from 'antd';
import { appMessage as message } from '../utils/appMessage';
import { Card } from '../types/card';
import { JOB_TYPE_LABEL, JobStatus, JobType } from '../types/job';
import {
  updateAllPrices, syncPricesToNaver, fetchStatusForCards, importFromNaver, classifyDrops, rerunMorningStep,
} from '../api/api';
import { DROP_STATUSES } from '../constants/constants';
import { CONFLICT_FALLBACK_REASON, currentStoreHeader } from '../http';
import type { RerunStep } from '../types/daily';
import { useDailyWorkflowStorage } from './UseDailyWorkflowStorage';
import { useJobRunner } from './UseJobRunner';
import { confirmSyncNaver } from '../components/syncConfirm/SyncConfirm';
import { useNavigation } from '../contexts/NavigationContext';

/** 재실행 단계 → 토스트 이름. */
export const RERUN_STEP_LABEL: Record<RerunStep, string> = {
  SYNC_STOCK:    '네이버 동기화',
  UPDATE_PRICES: '가격 받기',
  AUTO_INCREASE: '상승분 자동 반영',
};

/** 'started' = 잡 시작(진행바), 'empty' = 대상 없음(message 토스트), 'unsupported' = 옛 서버(404), 'failed' = 거절 · 오류(토스트). */
export type RerunResult = 'started' | 'empty' | 'unsupported' | 'failed';

// ─── useCardJobs ──────────────────────────────────────────────────────────────
// UseCardData.ts(393줄, useCards/useInlinePriceEdit/useCardJobs 세 관심사가 한 파일)를
// 파일별로 나눈 것 중 하나(2026-08-11, 로직 변경 없는 기계적 이동) — useCardData()가
// 이 세 훅을 조합한다.

export function useCardJobs(
  mergeResults: (results: Card[]) => void,
  reconcilePendingDrops: (results: Card[], opts?: { partial?: boolean }) => void,
  removePendingMany: (ids: number[]) => void,
  storage: ReturnType<typeof useDailyWorkflowStorage>,
  reloadCards: () => Promise<boolean | void>,
) {
  const [priceUpdating, setPriceUpdating] = useState(false);
  const [naverSyncing, setNaverSyncing]   = useState(false);
  const [importing, setImporting]         = useState(false);
  const [classifying, setClassifying]     = useState(false);
  const [activeJobId, setActiveJobId]     = useState<string | null>(null);

  // ── updateDone / naverDone: 초기값을 localStorage 에서 복원 ───────────────
  const [updateDone, setUpdateDoneRaw] = useState<boolean>(
    () => storage.readUpdateDone(),
  );
  const [naverDone, setNaverDoneRaw]   = useState<boolean>(
    () => storage.readNaverDone(),
  );

  const setUpdateDone = useCallback((v: boolean) => {
    storage.saveUpdateDone(v);
    setUpdateDoneRaw(v);
  }, [storage]);

  const setNaverDone = useCallback((v: boolean) => {
    storage.saveNaverDone(v);
    setNaverDoneRaw(v);
  }, [storage]);

  // 오늘 ① 조회에서 공통가로 대신 받은(매장 미인식) 카드 수(감사 #181) — ③ 반영 요약 · 결과 창에 보인다.
  const [storeUnresolvedCount, setStoreUnresolvedRaw] = useState<number>(() => storage.readStoreUnresolved?.() ?? 0);
  const setStoreUnresolvedCount = useCallback((n: number) => {
    storage.saveStoreUnresolved?.(n);
    setStoreUnresolvedRaw(n);
  }, [storage]);

  // ── 복원 시 안내 토스트 ────────────────────────────────────────────────────
  // 마운트 시점에 한 번만 실행. 오늘 진행분이 있으면 작업자에게 알린다.
  const notifiedRef = useRef(false);
  useEffect(() => {
    if (notifiedRef.current) return;
    notifiedRef.current = true;

    const restoredUpdate = storage.readUpdateDone();
    const restoredPending = storage.readPendingIds();

    if (restoredUpdate && restoredPending.length > 0) {
      message.info({
        key: 'wf-restored',
        content: `오늘 진행하던 작업을 복원했습니다. 하락 확인 대기 ${restoredPending.length}건이 남아 있습니다.`,
        duration: 6,
      });
    } else if (restoredUpdate) {
      message.info({
        key: 'wf-restored',
        content: '오늘 ① 가격 받기를 마친 상태에서 이어서 작업합니다.',
        duration: 4,
      });
    }

    // 어제 이전의 오래된 wf:* 키를 정리한다.
    storage.pruneOldKeys();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const resetJobState = useCallback(() => {
    setActiveJobId(null);
    setPriceUpdating(false);
    setNaverSyncing(false);
    setImporting(false);
    setClassifying(false);
  }, []);

  const runJob = useJobRunner(setActiveJobId);

  // 지금 도는 SYNC_NAVER 가 일부 카드만 보낸 선택 반영인가(감사 card-controltower#74) — 그렇다면 끝나도 naverDone 을
  // 세우지 않는다. "PriceHub 변경" 탭에서 몇 건만 보낸 뒤 ③ 이 "오늘 반영 완료"로 보여 나머지 전체 반영을 잊게 했다.
  // 잡은 한 번에 하나라(useJobRunner · 진행 중 차단) 실제로 시작된 호출에서만 덮어쓰는 ref 하나로 충분하다 — 시작 응답을
  // 기다리는 동안 다른 반영이 눌려 잠금에 거절되면 그 호출은 이 표시를 바꾸지 않는다(코덱 리뷰 #117 중간 1).
  const partialSyncRef = useRef(false);

  // ③ 확인 창(감사 #839) — 떠 있는 창 하나. 훅이 내려가면(로그아웃 · 세션 만료 · 매장 전환 재마운트) 창을 닫는다.
  const syncConfirmRef = useRef<AbortController | null>(null);
  useEffect(() => () => { syncConfirmRef.current?.abort(); syncConfirmRef.current = null; }, []);
  // 카드 컨텍스트(CardPageProvider)는 메뉴를 옮겨도 살아 있다 — 요약을 받는 사이 다른 메뉴로 가면 뒤늦게 창이 뜨고 반영까지 됐다. 화면(메뉴)이
  // 바뀌면 떠 있는 확인을 닫는다(코덱 리뷰 #98, 감사 #860).
  const { page } = useNavigation();
  useEffect(() => { syncConfirmRef.current?.abort(); syncConfirmRef.current = null; }, [page]);

  // 일부 카드만 다시 받는 가격 받기(반영 로그의 '실패 N건 가격 다시 받기', 유저 플로우 제안 1)의 잡 id. 그 잡이 끝나면 오늘 ① 완료 ·
  // 매장 미인식 수를 덮지 않고, 하락 확인 대기도 받은 카드만 고친다 — 전체 결과로 다시 세우면 다른 카드의 대기가 사라져 ③ 이 열렸다.
  const partialUpdateJobRef = useRef<string | null>(null);

  const startUpdatePrices = useCallback((selectedIds: number[], opts?: { partial?: boolean }) => {
    let startedJobId: string | null = null;
    return runJob(async () => {
      const r = await updateAllPrices(selectedIds);
      startedJobId = r.jobId;
      return r;
    }, '가격 받기 시작 중 오류가 발생했습니다.', () => {
      partialUpdateJobRef.current = opts?.partial ? startedJobId : null;
      setPriceUpdating(true);
    });
  }, [runJob]);

  // 아침 카드 실패 카드 재실행(백엔드 PR #95, 감사 card-controltower#796) — 지금 도는 재실행 잡. 끝나면 그 단계의 '오늘 완료' 표시
  // (① updateDone · ③ naverDone)를 세우지 않는다 — 상승분 자동 반영 재실행은 SYNC_NAVER 잡이라 그대로면 ③ 이 '오늘 반영 완료' 가 됐다.
  const rerunJobRef = useRef<{ jobId: string; step: RerunStep } | null>(null);
  const rerunInFlightRef = useRef(false);
  // null = 아직 모름, false = 옛 서버(404) — 화면은 다시 돌리기 버튼을 거두고 '이동만'(반영 로그 보기)으로.
  const [rerunSupported, setRerunSupported] = useState<boolean | null>(null);

  const startRerun = useCallback(async (step: RerunStep, ids: number[]): Promise<RerunResult> => {
    if (ids.length === 0 || rerunInFlightRef.current) return 'failed';
    rerunInFlightRef.current = true;
    try {
      const r = await rerunMorningStep(step, ids);
      setRerunSupported(true);
      if (!r.jobId) {
        // 대상 없음 — 상승분 자동 반영 조건(신선도 · 상승률 상한 · 출처 재조회)을 통과한 카드 0 등. 서버 요약을 그대로.
        message.info({ key: 'rerun', content: r.message ?? '다시 돌릴 대상이 없습니다.', duration: 8 });
        return 'empty';
      }
      rerunJobRef.current = { jobId: r.jobId, step };
      // 가격 받기 재실행은 일부 카드 다시 받기와 같다 — ① 완료 · 다른 카드의 하락 대기를 덮지 않는다(partialUpdateJobRef).
      if (step === 'UPDATE_PRICES') {
        partialUpdateJobRef.current = r.jobId;
        setPriceUpdating(true);
      }
      // 상승분 자동 반영 재실행은 네이버에 쓰는 SYNC_NAVER 잡 — 도는 동안 ③ 스토어 반영 · 롤백을 잠근다(isNaverDisabled 가 naverSyncing 을
      // 본다, 코덱 PR #94 중간, 감사 card-controltower#810). 끝나면 resetJobState 가 푼다.
      if (step === 'AUTO_INCREASE') setNaverSyncing(true);
      setActiveJobId(r.jobId);
      return 'started';
    } catch (e) {
      const res = (e as { response?: { status?: number; data?: { message?: unknown } } })?.response;
      const serverMessage = typeof res?.data?.message === 'string' && res.data.message !== '' ? res.data.message : null;
      if (res?.status === 404) {
        setRerunSupported(false);
        message.info({ key: 'rerun', content: '이 서버는 아직 실패 카드 다시 돌리기를 하지 않습니다 — 서버 업데이트 뒤 다시 해 주세요.', duration: 6 });
        return 'unsupported';
      }
      // 403(조회 전용)은 http 인터셉터가 띄운다.
      if (res?.status === 403) return 'failed';
      // 409 — 아침 체인 · 다른 재실행 · 같은 종류 잡 진행 중 · 자동 반영 매장 스위치 꺼짐. 서버 문구 그대로.
      if (res?.status === 409) {
        message.warning({ key: 'rerun', content: serverMessage ?? CONFLICT_FALLBACK_REASON, duration: 8 });
        return 'failed';
      }
      message.error({ key: 'rerun', content: serverMessage ?? '다시 돌리기 시작 중 오류가 발생했습니다.', duration: 6 });
      return 'failed';
    } finally {
      rerunInFlightRef.current = false;
    }
  }, []);

  const startFetchStatus = useCallback((ids: number[]) => {
    if (ids.length === 0) return Promise.resolve(false);
    return runJob(() => fetchStatusForCards(ids), '판매 상태 조회 시작 중 오류가 발생했습니다.');
  }, [runJob]);

  const startSyncNaver = useCallback((
    selectedIds: number[],
    pendingConfirmIds: Set<number>,
    opts?: { partial?: boolean },
  ) => {
    const unconfirmed = selectedIds.filter((id) => pendingConfirmIds.has(id));
    if (unconfirmed.length > 0) {
      Modal.warning({
        title: '확인 필요',
        content: `${unconfirmed.length}건의 가격 하락 상품에 대해 ✓/✗ 선택이 필요합니다.`,
        okText: '확인',
      });
      return Promise.resolve(false);
    }
    // 먼저 요약 확인 창(감사 card-controltower#538 · Q7) — 한 번 더 눌러야 실제 반영이 나간다. 창은 하나만(ref), 화면이 내려가면
    // 닫고, 요약 · 전송은 창을 연 시점 매장으로 고정한다(감사 #839 · #828 — ② 적용가 확인 창과 같은 방식).
    if (syncConfirmRef.current) return Promise.resolve(false);
    const ctrl = new AbortController();
    syncConfirmRef.current = ctrl;
    const store = currentStoreHeader();
    return confirmSyncNaver(selectedIds, { partial: opts?.partial, signal: ctrl.signal, store })
      .finally(() => { if (syncConfirmRef.current === ctrl) syncConfirmRef.current = null; })
      .then((ok) => (!ok || ctrl.signal.aborted ? false
      // 창이 떠 있는 동안 매장이 바뀌었으면(SUPER 전환) 처음 매장으로도 보내지 않는다 — 다시 확인하게(팀장 결정 2026-11-04, 시우 검수 #98 F4a ·
      // ② 와 같은 규칙). storeOverride 는 요약 · 전송이 같은 매장을 가리키게 하는 데만 쓴다.
      : currentStoreHeader() !== store ? (message.warning({ key: 'sync-preview', content: '매장이 바뀌어 반영하지 않았습니다 — 다시 확인해 주세요', duration: 8 }), false)
      : runJob(() => syncPricesToNaver(selectedIds, { store }), 'Naver 반영 시작 중 오류가 발생했습니다.', () => {
        // onStarted 는 잠금을 얻고 시작 API 가 성공한 호출에서만 불린다.
        partialSyncRef.current = !!opts?.partial;
        setNaverSyncing(true);
        // 409(확정 대기 1,000건 잘림 · 잡 락 — 백엔드 PR #82)는 "반영 막힘 — <사유>" 로(감사 #700).
      }, { conflictLabel: '반영 막힘' })));
  }, [runJob]);

  const startImportProducts = useCallback(() =>
    runJob(() => importFromNaver(), '상품 가져오기 시작 중 오류가 발생했습니다.', () => setImporting(true)),
    [runJob]);

  // ① 가격 받기 완료 후 CardPageContext가 자동으로 이어서 호출한다.
  const startClassifyDrops = useCallback((dropIds: number[]) =>
    runJob(() => classifyDrops(dropIds), '시세 대조 분류 시작 중 오류가 발생했습니다.', () => setClassifying(true)),
    [runJob]);

  // job.type별 완료 처리 — 예전엔 `if (job.type === 'X') {...}`를 나란히 늘어놓아서
  // (실제로는 서로 배타적인데도) 매번 전부 평가했다. Strategy 패턴으로 job.type → handler
  // 맵을 한 곳에 등록해두고 딱 하나만 실행한다(2026-08-11). 각 핸들러 본문은 원래
  // if 블록과 완전히 동일 — 동작 변경 없이 구조만 바꾼 리팩터링.
  const handleJobComplete = useCallback((job: JobStatus) => {
    resetJobState();
    const rerun = rerunJobRef.current != null && rerunJobRef.current.jobId === job.jobId ? rerunJobRef.current : null;
    if (rerun) rerunJobRef.current = null;
    // 실패(또는 서버에서 사라진 잡)는 잠금 · 로딩만 풀고 종류별 완료 처리(updateDone · naverDone · 성공 토스트)는 하지
    // 않는다 — JobProgressBar 가 FAILED 에도 onComplete 를 부르게 된 뒤로(감사 card-controltower#84) 여기로 온다.
    if (job.status === 'FAILED') {
      partialSyncRef.current = false;
      if (partialUpdateJobRef.current === job.jobId) partialUpdateJobRef.current = null;
      message.error({ key: 'job-failed', content: `${JOB_TYPE_LABEL[job.type] ?? '작업'} 실패 — ${job.error ?? '알 수 없는 오류'}`, duration: 6 });
      return;
    }

    // 실패 카드 다시 돌리기(동기화 · 상승분 자동 반영) — 받은 카드만 고치고 완료 토스트. 그 단계의 '오늘 완료' 표시는 건드리지 않는다
    // (일부 카드 실행 — 백엔드도 updateDone · 아침 요약 · 전체 동기화 완료에 넣지 않는다). 가격 받기 재실행은 아래 partial 경로로.
    if (rerun && rerun.step !== 'UPDATE_PRICES') {
      // 동기화가 스토어에서 지워진 상품을 정리했으면(deletedCount — 서버가 소프트 삭제) mergeResults(치환만)로는 그 카드가 목록에
      // 남는다 — 목록을 통째로 다시 받는다(코덱 PR #94 중간, 감사 card-controltower#809). 아니면 받은 카드만 고친다.
      const deleted = job.deletedCount ?? 0;
      if (deleted > 0) void reloadCards();
      else if (job.results) mergeResults(job.results);
      const parts = [`성공 ${(job.successCount ?? 0).toLocaleString()}건`];
      if ((job.failCount ?? 0) > 0) parts.push(`실패 ${job.failCount.toLocaleString()}건`);
      if (deleted > 0) parts.push(`스토어에서 삭제된 상품 ${deleted.toLocaleString()}건 정리`);
      const skipped = job.skippedCount ?? 0;
      if (skipped > 0) parts.push(`건너뜀 ${skipped.toLocaleString()}건`);
      const content = `${RERUN_STEP_LABEL[rerun.step]} 다시 돌리기 완료 — ${parts.join(' / ')}`;
      if ((job.failCount ?? 0) > 0) {
        message.warning({ key: 'rerun-done', content: `${content} — 남은 실패는 반영 로그에서 확인하세요`, duration: 8 });
      } else {
        message.success({ key: 'rerun-done', content, duration: 5 });
      }
      return;
    }

    const handlers: Partial<Record<JobType, () => void>> = {
      UPDATE_PRICES: () => {
        const partial = partialUpdateJobRef.current != null && partialUpdateJobRef.current === job.jobId;
        if (partial) partialUpdateJobRef.current = null;
        if (!job.results) return;
        mergeResults(job.results);
        if (partial) {
          // 일부 다시 받기 — 받은 카드의 대기만 고치고(하락이면 넣고 아니면 뺀다) ① 완료 · 매장 미인식 수는 그대로 둔다.
          reconcilePendingDrops(job.results, { partial: true });
          message.success({ key: 'update-success', content: `가격 다시 받기 완료 — ${job.results.length.toLocaleString()}건`, duration: 4 });
          return;
        }
        setUpdateDone(true);   // ← localStorage 에도 저장됨
        // 공통가로 대신 받은 카드 수 — 0 이어도 저장해 어제 · 지난 조회 값이 남지 않게(감사 #181).
        const unresolved = job.storeUnresolvedCount ?? 0;
        setStoreUnresolvedCount(unresolved);
        if (unresolved > 0) {
          message.warning({
            key: 'store-unresolved',
            content: `${unresolved.toLocaleString()}건은 PriceHub 가 이 매장을 알아보지 못해 매장 가격 대신 PriceHub 가격을 받았습니다(PriceHub 가격 · 매장 미인식) — 반영 전에 확인하세요.`,
            duration: 10,
          });
        }

        // 이번 조회 결과 기준으로 대기 목록을 재조정한다 — 더 이상 하락이 아닌 카드는
        // 빼고, 여전히/새로 하락인 카드만 남긴다.
        reconcilePendingDrops(job.results);

        const dropIds = job.results
          .filter((c) => DROP_STATUSES.includes(c.priceChangeStatus))
          .map((c) => c.id);

        if (dropIds.length > 0) {
          // 실제 검토 안내는 시세 대조 분류(CLASSIFY_DROPS) 완료 후 토스트가 맡는다.
          message.info({ key: 'drop-warning', content: `하락 카드 ${dropIds.length}건 — 타 샵 시세와 대조하는 중입니다.`, duration: 4 });
        } else {
          message.success({ key: 'update-success', content: `가격 받기 완료 — ${job.results.length}건`, duration: 3 });
        }
      },
      SYNC_NAVER: () => {
        if (!job.results) return;
        mergeResults(job.results);
        // 선택 반영(일부)은 오늘의 ③ 을 끝낸 것이 아니다(감사 #74).
        if (!partialSyncRef.current) setNaverDone(true);    // ← localStorage 에도 저장됨
        partialSyncRef.current = false;
        // 확정 대기 · 요청 대기로 건너뛴 카드는 실패가 아니다(백엔드 PR #76 skippedCount, 감사 #538) — 따로 센다.
        const skipped = job.skippedCount ?? 0;
        message.success({
          key: 'naver-success',
          content: `Naver 반영 완료: ${job.successCount}건 성공 / ${job.failCount}건 실패${skipped > 0 ? ` / 건너뜀 ${skipped}건(확정 · 요청 대기)` : ''}`,
          duration: skipped > 0 ? 6 : 3,
        });
      },
      FETCH_STATUS: () => {
        if (!job.results) return;
        mergeResults(job.results);
        message.success({ key: 'status-success', content: '판매 상태 조회 완료', duration: 3 });
      },
      IMPORT_PRODUCTS: () => {
        // 신규 등록된 카드는 기존 목록에 없던 id라 mergeResults(치환만 함)로는 안 들어온다 —
        // 전체를 다시 불러온다.
        reloadCards();
        // deletedCount(스토어에서 삭제된 상품 정리)를 안 보여주면, 신규 등록 0건일 때
        // "신규 등록 0건"만 뜨고 끝나서 실제로는 정리가 있었는데도 버튼이 반응 안 하는
        // 것처럼 보인다(2026-08-19, 사용자가 실제로 겪음).
        const parts = [`신규 등록 ${job.successCount}건`];
        if (job.failCount > 0) parts.push(`판매자 상품코드 중복 경고 ${job.failCount}건`);
        if (job.deletedCount > 0) parts.push(`스토어에서 삭제된 상품 ${job.deletedCount}건 정리`);
        message.success({ key: 'import-success', content: parts.join(' / '), duration: 4 });
      },
      CLASSIFY_DROPS: () => {
        if (!job.results) return;
        mergeResults(job.results);   // 각 카드에 dropBucket 등이 반영됨

        // NO_REFERENCE = 대조 불가 → 확인 대기 목록에서 즉시 제거(③ 을 막지 않게). ③ 스토어 반영은 이 카드들을
        // 대상에서 뺀다(DailyWorkflowBar, 2026-10-21 — 예전엔 검증 없이 <매장> 가격(옛 추천가) 그대로 반영됐다).
        // IN_RANGE는 사용자가 "일괄 적용"을 눌러야 하므로 대기 목록에 남긴다.
        const noRefIds = job.results.filter((c) => c.dropBucket === 'NO_REFERENCE').map((c) => c.id);
        if (noRefIds.length > 0) removePendingMany(noRefIds);

        const suspectN = job.results.filter((c) => c.dropBucket === 'SUSPECT').length;
        const inRangeN = job.results.filter((c) => c.dropBucket === 'IN_RANGE').length;
        message.info({
          key: 'classify-done',
          content: `시세 대조 완료 — 확인 필요 ${suspectN}건 · 시세 범위 내 ${inRangeN}건 · 참조 없음 ${noRefIds.length}건`,
          duration: 6,
        });
      },
    };

    handlers[job.type]?.();
  }, [resetJobState, mergeResults, reconcilePendingDrops, removePendingMany, setUpdateDone, setNaverDone, reloadCards,
    setStoreUnresolvedCount]);

  const closeJob = useCallback(() => resetJobState(), [resetJobState]);

  return useMemo(() => ({
    priceUpdating, naverSyncing, importing, classifying, activeJobId, setActiveJobId,
    updateDone, naverDone, storeUnresolvedCount,
    startUpdatePrices, startFetchStatus, startSyncNaver, startImportProducts, startClassifyDrops,
    startRerun, rerunSupported,
    handleJobComplete, closeJob,
  }), [
    priceUpdating, naverSyncing, importing, classifying, activeJobId, setActiveJobId,
    updateDone, naverDone, storeUnresolvedCount,
    startUpdatePrices, startFetchStatus, startSyncNaver, startImportProducts, startClassifyDrops,
    startRerun, rerunSupported,
    handleJobComplete, closeJob,
  ]);
}
